import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './PartnerMeetingDisplay.css';
import { getTodayYYYYMMDD } from '../../utils/dateUtils';


const DEFAULT_PARTNERS = [
  'AMARSINGH THAKUR',
  'DHIRAJ HALLE',
  'GURABASAPPA S Y',
  'GURURAJ CHINCHURE',
  'KARABASAPPA MANTAGI',
  'LAXMIKANTH CHINCHURE',
  'RAHUL HADAPAD',
  'MALLIKARJUN HALAKE',
  'NOOR SHAIKH',
  'SATISH BERAD',
  'SIDDARAM DHAGE',
  'SHAM PUJARI',
  'SHRISHAIL KUMBAR',
  'MARUTI TADKAL',
];

const API_BASE_URL =
  (process.env.REACT_APP_API_BASE_URL && process.env.REACT_APP_API_BASE_URL.trim()) ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const PartnerMeetingDisplay = () => {
  const [availablePartners, setAvailablePartners] = useState(DEFAULT_PARTNERS);
  const [startDate, setStartDate] = useState(getTodayYYYYMMDD());
  const [endDate, setEndDate] = useState(getTodayYYYYMMDD());
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const partnersList = useMemo(() => {
    const deduped = new Set();
    return availablePartners
      .map((name) => (typeof name === 'string' ? name.trim() : ''))
      .filter((name) => {
        if (!name) {
          return false;
        }
        const key = name.toUpperCase();
        if (deduped.has(key)) {
          return false;
        }
        deduped.add(key);
        return true;
      });
  }, [availablePartners]);

  const fetchData = useCallback(async (partnersOverride) => {
    setLoading(true);
    setError('');

    const partnersToFetch = Array.isArray(partnersOverride) ? partnersOverride : partnersList;

    if (partnersToFetch.length === 0) {
      setLoading(false);
      setError('Please enter at least one partner name.');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/fetch-data`, {
        partners: partnersToFetch,
        startDate,
        endDate,
      });

      setTableData(response.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [partnersList, startDate, endDate]);

  useEffect(() => {
    let isMounted = true;

    const loadPartners = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/partners`);
        const partnerList = Array.isArray(response.data)
          ? response.data.map((name) => (typeof name === 'string' ? name.trim() : '')).filter(Boolean)
          : [];

        if (!isMounted) {
          return;
        }

        if (partnerList.length === 0) {
          setAvailablePartners(DEFAULT_PARTNERS);
          return;
        }

        const merged = [];
        const seenUpper = new Set();
        const pushUnique = (name) => {
          const trimmed = typeof name === 'string' ? name.trim() : '';
          if (!trimmed) {
            return;
          }
          const key = trimmed.toUpperCase();
          if (seenUpper.has(key)) {
            return;
          }
          seenUpper.add(key);
          merged.push(trimmed);
        };

        partnerList.forEach(pushUnique);
        DEFAULT_PARTNERS.forEach(pushUnique);

        setAvailablePartners(merged);
      } catch (err) {
        console.error('Error loading partner names:', err);
        if (!isMounted) {
          return;
        }
        setAvailablePartners(DEFAULT_PARTNERS);
      }
    };

    loadPartners();

    return () => {
      isMounted = false;
    };
  }, [fetchData]);

  // Note: fetch is now manual via the button to avoid continuous auto-loading

  const toNumber = (value) => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const calculateRowTotal = (row) =>
    toNumber(row.od) +
    toNumber(row.gl) +
    toNumber(row.cd) +
    toNumber(row.tbd) +
    toNumber(row.dc);

  const basePartners = useMemo(() => {
    if (partnersList.length > 0) {
      return partnersList;
    }
    if (availablePartners.length > 0) {
      return availablePartners;
    }
    return DEFAULT_PARTNERS;
  }, [partnersList, availablePartners]);

  const displayRows = useMemo(() => {
    const partnerKeys = new Set(basePartners.map((name) => name.trim().toUpperCase()));
    const dataMap = new Map();

    tableData.forEach((row) => {
      const key = row.partnerName ? row.partnerName.trim().toUpperCase() : '';
      if (key) {
        dataMap.set(key, row);
      }
    });

    const partnerRows = basePartners.map((name) => {
      const key = name.trim().toUpperCase();
      const existingRow = dataMap.get(key);
      if (existingRow) {
        return existingRow;
      }
      return {
        partnerName: name,
        od: 0,
        gl: 0,
        cd: 0,
        tbd: 0,
        dc: 0,
      };
    });

    const extraRows = tableData.filter((row) => {
      const key = row.partnerName ? row.partnerName.trim().toUpperCase() : '';
      return key && !partnerKeys.has(key);
    });

    return [...partnerRows, ...extraRows];
  }, [basePartners, tableData]);

  const calculateGrandTotal = (rows) => {
    const totals = { od: 0, gl: 0, cd: 0, tbd: 0, dc: 0, total: 0 };

    rows.forEach((row) => {
      totals.od += toNumber(row.od);
      totals.gl += toNumber(row.gl);
      totals.cd += toNumber(row.cd);
      totals.tbd += toNumber(row.tbd);
      totals.dc += toNumber(row.dc);
      totals.total += calculateRowTotal(row);
    });

    return totals;
  };

  const grandTotal = useMemo(() => calculateGrandTotal(displayRows), [displayRows]);

  const handlePrint = () => {
    const printContent = document.getElementById('table-container').innerHTML;
    const printWindow = window.open('', '_blank');
    const partnersJoined = partnersList.join(', ') || '—';
    printWindow.document.write(`
      <html>
        <head>
          <title>Partnerwise Interest Meeting Data</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              padding: 20px;
              margin: 0;
            }
            .print-container {
              width: 100%;
              max-width: 1000px;
              margin: 0 auto;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            h2 {
              text-align: center;
              margin: 0 0 10px 0;
              font-size: 24px;
            }
            .meta {
              text-align: center;
              margin: 0 0 20px 0;
            }
            h4 {
              margin: 0;
              font-size: 16px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 0 auto;
              font-size: 14px;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            }
            th, td {
              border: 1px solid #ccc;
              padding: 10px;
              text-align: left;
            }
            th {
              background-color: #f4f4f4;
              font-weight: bold;
              text-align: center;
            }
            td {
              text-align: center;
            }
            .font-bold {
              font-weight: bold;
              background-color: #f0f0f0;
            }
            @media print {
              @page {
                size: A4;
                margin: 1.5cm;
              }
              body {
                padding: 0;
                min-height: auto;
              }
              .print-container {
                max-width: 100%;
                margin: 0;
              }
              table {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <h2>Partnerwise Interest Meeting Data</h2>
            <div class="meta">
              <h4>Date Range: ${startDate} to ${endDate}</h4>
            </div>
            ${printContent}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="container">
      <h2 className="title">Partner Meeting Data</h2>

      <div className="controls">
        <div className="date-input">
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="date-input">
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button onClick={() => fetchData()} className="fetch-button">Fetch Data</button>
      </div>

      {loading ? (
        <div className="loading">Loading data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <div id="table-container" className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Partner Name</th>
                <th>OD</th>
                <th>GL</th>
                <th>CD</th>
                <th>TBD</th>
                <th>DC</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((row, index) => (
                <tr key={index}>
                  <td>{row.partnerName || 'N/A'}</td>
                  <td>{toNumber(row.od).toFixed(2)}</td>
                  <td>{toNumber(row.gl).toFixed(2)}</td>
                  <td>{toNumber(row.cd).toFixed(2)}</td>
                  <td>{toNumber(row.tbd).toFixed(2)}</td>
                  <td>{toNumber(row.dc).toFixed(2)}</td>
                  <td>{calculateRowTotal(row).toFixed(2)}</td>
                </tr>
              ))}

              {/* Grand Total Row */}
              <tr className="font-bold">
                <td>Grand Total</td>
                <td>{grandTotal.od.toFixed(2)}</td>
                <td>{grandTotal.gl.toFixed(2)}</td>
                <td>{grandTotal.cd.toFixed(2)}</td>
                <td>{grandTotal.tbd.toFixed(2)}</td>
                <td>{grandTotal.dc.toFixed(2)}</td>
                <td>{grandTotal.total.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <button onClick={handlePrint} className="print-button">Print Table</button>
    </div>
  );
};

export default PartnerMeetingDisplay;

